import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SKIP_SUCCESS_TOAST } from '../../@interceptors/toast-interceptor';
import {
  CreateGroupPayload,
  CustomerGroup,
  GroupMember,
  MembersPayload,
  UpdateGroupPayload,
} from '../../models/customer-group';

interface ApiResponse<T> { success: boolean; message: string; data: T; }
interface PagedResponse<T> { content: T[]; totalElements: number; totalPages: number; currentPage: number; hasNext: boolean; }

@Injectable({ providedIn: 'root' })
export class CustomerGroupsService {
  private base = `${environment.url}/customer-groups`;
  private http = inject(HttpClient);

  getGroups(params?: { search?: string; sortBy?: string; sortDirection?: string; page?: number; size?: number }):
    Observable<ApiResponse<PagedResponse<CustomerGroup>>> {
    let httpParams = new HttpParams();
    if (params?.search)        httpParams = httpParams.set('search', params.search);
    if (params?.sortBy)        httpParams = httpParams.set('sortBy', params.sortBy);
    if (params?.sortDirection) httpParams = httpParams.set('sortDirection', params.sortDirection);
    if (params?.page != null)  httpParams = httpParams.set('page', String(params.page));
    if (params?.size  != null) httpParams = httpParams.set('size', String(params.size));
    return this.http.get<ApiResponse<PagedResponse<CustomerGroup>>>(this.base, { params: httpParams });
  }

  getGroup(id: number): Observable<ApiResponse<CustomerGroup>> {
    return this.http.get<ApiResponse<CustomerGroup>>(`${this.base}/${id}`);
  }

  createGroup(payload: CreateGroupPayload): Observable<ApiResponse<CustomerGroup>> {
    return this.http.post<ApiResponse<CustomerGroup>>(this.base, payload);
  }

  updateGroup(id: number, payload: UpdateGroupPayload): Observable<ApiResponse<CustomerGroup>> {
    return this.http.patch<ApiResponse<CustomerGroup>>(`${this.base}/${id}`, payload);
  }

  deleteGroup(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.base}/${id}`);
  }

  getMembers(groupId: number, params?: { page?: number; size?: number; search?: string }):
    Observable<ApiResponse<PagedResponse<GroupMember>>> {
    let httpParams = new HttpParams();
    if (params?.page != null) httpParams = httpParams.set('page', String(params.page));
    if (params?.size  != null) httpParams = httpParams.set('size', String(params.size));
    if (params?.search)        httpParams = httpParams.set('search', params.search);
    return this.http.get<ApiResponse<PagedResponse<GroupMember>>>(`${this.base}/${groupId}/members`, { params: httpParams });
  }

  updatePlanDiscounts(groupId: number, planDiscounts: { pricingPlanId: number; discountPercentage: number }[]): Observable<ApiResponse<CustomerGroup>> {
    return this.http.put<ApiResponse<CustomerGroup>>(`${this.base}/${groupId}/plan-discounts`, { discounts: planDiscounts });
  }

  uploadBadge(groupId: number, file: File): Observable<ApiResponse<CustomerGroup>> {
    const form = new FormData();
    form.append('image', file);
    return this.http.put<ApiResponse<CustomerGroup>>(`${this.base}/${groupId}/badge`, form, { context: this.silent() });
  }

  deleteBadge(groupId: number): Observable<ApiResponse<CustomerGroup>> {
    return this.http.delete<ApiResponse<CustomerGroup>>(`${this.base}/${groupId}/badge`, { context: this.silent() });
  }

  addMembers(groupId: number, payload: MembersPayload): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.base}/${groupId}/members`, payload);
  }

  removeMembers(groupId: number, payload: MembersPayload): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.base}/${groupId}/members/remove`, payload);
  }

  /** Badge calls follow a create/update that already shows its own toast. */
  private silent() {
    return new HttpContext().set(SKIP_SUCCESS_TOAST, true);
  }
}
